/* Game shell: BoardScroll + global keyboard handler + BoardEffects.

   Hint, Keyboard, and ShareGrid moved out into App.tsx's region
   layout (Phase 1.5). Keeping them in one component made the board
   hard to scroll-anchor on tall puzzles; the three-region shell
   (top / middle / bottom) lets the playfield take the flex-grow
   middle and scroll on its own when the puzzle outgrows the
   viewport. */

import { useEffect } from "preact/hooks";
import {
  backspace,
  buyHint,
  moveCursor,
  status,
  submit,
  typeLetter,
} from "../game/state";
import { BoardEffects } from "../effects/BoardEffects";
import { BoardScroll } from "./BoardScroll";

export function Game() {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      // Ignore typing into actual inputs (none today; future-proof).
      const target = e.target as HTMLElement | null;
      if (target && (target.tagName === "INPUT" || target.tagName === "TEXTAREA")) return;
      if (e.metaKey || e.ctrlKey || e.altKey) return;

      if (e.key === "Enter") {
        // During gameplay Enter always submits the guess, even when a
        // button has focus from a prior click — otherwise tapping an
        // on-screen letter and then pressing Enter would re-type the
        // letter instead of committing the row. Post-terminal Enter
        // is a no-op (the next puzzle drops at 00:00 UTC; there's no
        // restart affordance).
        if (status.value !== "playing") return;
        e.preventDefault();
        submit();
        return;
      }
      if (e.key === "Backspace") {
        e.preventDefault();
        backspace();
        return;
      }
      if (e.key === "ArrowLeft") {
        e.preventDefault();
        moveCursor(-1);
        return;
      }
      if (e.key === "ArrowRight") {
        e.preventDefault();
        moveCursor(1);
        return;
      }
      if (e.key === "?" && status.value === "playing") {
        e.preventDefault();
        buyHint();
        return;
      }
      if (e.key.length === 1 && /[a-zA-Z]/.test(e.key)) {
        e.preventDefault();
        typeLetter(e.key);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  return (
    <div class="game">
      <BoardScroll />
      <BoardEffects />
    </div>
  );
}
